import {MainFileImportStatus, MainFileType} from "../redux/Main/File/types";
import {MediaSourceImportType} from "./mediaSourceEvents";
import {formatBytes} from "./functions";

/**
 * The editor's view of a file picked from a media source: in the post's media at once (by the
 * import's uuid, which becomes the media uuid), then filled in when the file has arrived.
 */
export const pendingFileFromImport = (item: MediaSourceImportType, mimeType?: string, size?: number): MainFileType => ({
    uuid: item.uuid,
    url: '',
    fileName: item.fileName,
    fileSize: size,
    formattedSize: size !== undefined ? formatBytes(size) : undefined,
    contentType: mimeType,
    enabled: true,
    importStatus: MainFileImportStatus.PENDING,
    actions: {canEdit: false, canRemove: true},
});

export const pendingImportUuids = (files: MainFileType[]): string[] =>
    (files || [])
        .filter(file => file.importStatus === MainFileImportStatus.PENDING && file.uuid !== undefined)
        .map(file => file.uuid as string);

/**
 * Apply import results to the files; null when none of them changed.
 */
export const applyImportResults = (files: MainFileType[], results: MediaSourceImportType[]): MainFileType[] | null => {
    const byUuid = new Map((results || []).map(result => [result.uuid, result]));
    let changed = false;

    const updated = (files || []).map(file => {
        const result = file.uuid !== undefined ? byUuid.get(file.uuid) : undefined;
        if (result === undefined || file.importStatus !== MainFileImportStatus.PENDING) {
            return file;
        }

        if (result.status === 'imported') {
            changed = true;
            const media = result.media || {};
            return {
                ...file,
                ...media,
                uuid: file.uuid,
                sort: file.sort,
                formattedSize: media.fileSize !== undefined ? formatBytes(media.fileSize) : file.formattedSize,
                enabled: true,
                importStatus: undefined,
                importError: undefined,
                actions: {canEdit: false, ...(media.actions || {}), canRemove: true},
            } as MainFileType;
        }

        if (result.status === 'failed') {
            changed = true;
            return {
                ...file,
                enabled: false,
                importStatus: MainFileImportStatus.FAILED,
                importError: result.errorMessage || undefined,
            };
        }

        return file;
    });

    return changed ? updated : null;
};
